import AppointmentReservationPanel from "@/features/settings/preferences/AppointmentReservationPanel.jsx";
// src/pages/appointments/details/AppointmentDetails.jsx
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import ContextFeedbackLauncher from "@/components/feedback/ContextFeedbackLauncher.jsx";
import DoctorResultCard from "@/components/doctor/DoctorResultCard.jsx";
import ehrIconPlain from "@/icons/afrimed/EHR.svg";
import { supabase } from "@/services/supabaseClient";
import { ehrStatusFromServer } from "@/services/confirmedEhrRequest.mjs";
import { loadPatientAppointmentSnapshot } from "@/services/patientAppointmentSnapshots.js";
import videoConsultationService from "@/services/videoConsultationService.js";
import {
  createAppointmentNotification,
  grantAppointmentEhrAccess,
  loadPatientAppointmentEhrAccessState,
  resolveDoctorAuthUserId,
} from "@/services/ehrAccessService.js";
import { sendAppointmentEventEmail } from "@/services/appointmentEmailService.js";
import {
  buildFavoriteDoctorIds,
  isFavoriteDoctor,
  subscribeDoctorFavorites,
} from "@/services/doctorFavorites.js";
import { getAppointmentLifecycleBucket } from "@/services/appointmentLifecycle.js";
import {
  acquireSafeConsultationStream,
  stopConsultationStream,
} from "@/services/consultationDeviceSafety.js";

import AppointmentCancelDialog from "@/pages/appointments/details/AppointmentCancelDialog.jsx";
import AppointmentDeleteDialog from "@/pages/appointments/details/AppointmentDeleteDialog.jsx";
import AppointmentMissedDialog from "@/pages/appointments/details/AppointmentMissedDialog.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";

const ICONS = {
  meet: "/icons/afrimed-video.svg",
  evisit: "/icons/appointments/afrimed-EVisit.svg",
  chat: "/icons/appointments/chat.svg",
  call: "/icons/appointments/call.svg",
  card: "/icons/appointments/credit_card.svg",
  ehr: ehrIconPlain,
  requestEhr: "/icons/doctor/appointments/Request_EHR.svg",
  grantedEhr: "/icons/doctor/appointments/EHR/EHR_Tonal.svg",
  accessRequested: "/icons/doctor/appointments/access_requested.svg",
  requestGranted: "/icons/doctor/appointments/EHR/request_granted.svg",
};

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function restoreAppointmentDraft(id, locationState) {
  // 1) Snapshot by id
  try {
    const raw = sessionStorage.getItem(`afrimed:appointment:${String(id)}`);
    const parsed = raw ? safeJSONParse(raw) : null;
    if (parsed && locationState && Object.keys(locationState || {}).length) {
      return mergeAppointmentSnapshot(parsed, locationState);
    }
    if (parsed) return parsed;
  } catch {}

  // 2) Prefer router state (fresh navigation) if there is no saved snapshot.
  if (locationState && Object.keys(locationState || {}).length) return locationState;

  // 3) Last snapshot fallback
  try {
    const raw = sessionStorage.getItem("afrimed:appointment:last");
    const parsed = raw ? safeJSONParse(raw) : null;
    const parsedId = String(parsed?.appointmentId || parsed?.id || "").trim();
    if (parsed && parsedId && parsedId === String(id || "").trim()) return parsed;
  } catch {}

  return null;
}

function persistAppointment(id, payload) {
  if (!payload) return;
  try {
    sessionStorage.setItem(`afrimed:appointment:${String(id)}`, JSON.stringify(payload));
    sessionStorage.setItem("afrimed:appointment:last", JSON.stringify(payload));
  } catch {
    // ignore
  }
}

async function primeConsultationMediaPermission({ appointmentId = "" } = {}) {
  const stream = await acquireSafeConsultationStream({
    appointmentId,
    route: window.location?.pathname || "",
    preferVideo: true,
  });
  stopConsultationStream(stream);
}

function logVideoConsultationStartDebug(payload = {}) {
  const host = String(window.location?.host || "").toLowerCase();
  const isDebugHost =
    host.includes("localhost") ||
    host.includes("127.0.0.1") ||
    host.includes("staging") ||
    host.includes("netlify.app");

  if (!isDebugHost) return;

  console.warn("[video-consultation-start]", {
    role: "patient",
    route: window.location?.pathname || "",
    appointmentId: "",
    appointmentStatus: "",
    hasVideoSession: false,
    videoSessionStatus: "",
    action: "",
    errorName: "",
    errorMessage: "",
    ...payload,
  });
}

function mergeDoctor(primaryDoctor, fallbackDoctor) {
  const primary = primaryDoctor || {};
  const fallback = fallbackDoctor || {};
  const doctorId = primary.id || fallback.id || null;
  const favoriteDoctorIds = buildFavoriteDoctorIds(primary, fallback);
  const primaryName = String(primary.name || "").trim();
  const fallbackName = String(fallback.name || "").trim();
  const primarySpecialty = String(primary.specialty || "").trim();
  const fallbackSpecialty = String(fallback.specialty || "").trim();
  const primaryYears = String(primary.yearsExperience || "").trim();
  const fallbackYears = String(fallback.yearsExperience || "").trim();
  return {
    ...fallback,
    ...primary,
    id: doctorId,
    name:
      primaryName && primaryName !== "Doctor"
        ? primaryName
        : fallbackName || primaryName || "Doctor",
    specialty:
      primarySpecialty && primarySpecialty !== "Doctor"
        ? primarySpecialty
        : fallbackSpecialty || primarySpecialty || "Doctor",
    yearsExperience:
      primaryYears && primaryYears !== "—"
        ? primaryYears
        : fallbackYears || primaryYears || "—",
    rating:
      primary.rating && primary.rating !== "—"
        ? primary.rating
        : fallback.rating || "—",
    avatar: primary.avatar || fallback.avatar || "",
    serviceLabel: primary.serviceLabel || fallback.serviceLabel || "Consultation",
    serviceLabelPrice: primary.serviceLabelPrice || fallback.serviceLabelPrice || "",
    serviceName: primary.serviceName || fallback.serviceName || "",
    serviceNamePrice: primary.serviceNamePrice || fallback.serviceNamePrice || "",
    currency: primary.currency || fallback.currency || "ZAR",
    languages: primary.languages || fallback.languages || "",
    hospital: primary.hospital || fallback.hospital || "Consultation location",
    address: primary.address || fallback.address || "",
    facility_id: primary.facility_id || fallback.facility_id || null,
    facility: primary.facility || fallback.facility || null,
    isFavorite:
      primary.isFavorite === true ||
      fallback.isFavorite === true ||
      (doctorId ? isFavoriteDoctor(favoriteDoctorIds) : false),
  };
}

function mergeAppointmentSnapshot(primarySnapshot, fallbackSnapshot) {
  if (!primarySnapshot) return fallbackSnapshot || null;
  if (!fallbackSnapshot) return primarySnapshot;

  return {
    ...fallbackSnapshot,
    ...primarySnapshot,
    doctor: mergeDoctor(primarySnapshot.doctor, fallbackSnapshot.doctor),
    amountPaid:
      primarySnapshot.amountPaid != null ? primarySnapshot.amountPaid : fallbackSnapshot.amountPaid,
    currency: primarySnapshot.currency || fallbackSnapshot.currency || "ZAR",
    paymentLabel: primarySnapshot.paymentLabel || fallbackSnapshot.paymentLabel || "",
    paymentMethodLabel:
      primarySnapshot.paymentMethodLabel || fallbackSnapshot.paymentMethodLabel || "",
    insurancePaymentMode:
      primarySnapshot.insurancePaymentMode || fallbackSnapshot.insurancePaymentMode || "self_pay",
    insuranceProvider:
      primarySnapshot.insuranceProvider || fallbackSnapshot.insuranceProvider || "",
    insuranceContextLabel:
      primarySnapshot.insuranceContextLabel || fallbackSnapshot.insuranceContextLabel || "",
  };
}

function resolveInsuranceContextLabel(hydrated, t = (_, fallback) => fallback) {
  const existing = String(
    hydrated?.insuranceContextLabel || hydrated?.paymentMeta?.insurance_context_label || "",
  ).trim();
  if (existing) return existing;
  const mode = String(
    hydrated?.insurancePaymentMode || hydrated?.paymentMeta?.insurance_payment_mode || "",
  )
    .trim()
    .toLowerCase();
  if (mode === "insurance") {
    const provider = String(
      hydrated?.insuranceProvider || hydrated?.paymentMeta?.insurance_provider || "",
    ).trim();
    return provider
      ? t("appointments.details.insurance.provider", "Insurance: {provider}", { provider })
      : t("appointments.details.insurance.generic", "Insurance selected");
  }
  return t("appointments.details.insurance.selfPay", "Self-pay");
}

function normalizeMode(restored) {
  const raw =
    restored?.mode ??
    restored?.visitType ??
    restored?.appointmentType ??
    (restored?.isEVisit ? "evisit" : null);

  if (raw === "evisit" || raw === "virtual") return "evisit";
  if (raw === "inperson" || raw === "in-person") return "inperson";

  // fallback inference
  if (restored?.facilityId || restored?.facility?.id) return "inperson";
  return "evisit";
}

function normalizeAppointmentStatus(value) {
  return String(value || "").trim().toLowerCase();
}

function resolveAppointmentStartMs(snapshot) {
  const rawDateTime = String(snapshot?.appointment_date || "").trim();
  if (rawDateTime) {
    const direct = new Date(rawDateTime).getTime();
    if (Number.isFinite(direct)) return direct;
  }

  const dateISO = String(
    snapshot?.selectedDate || snapshot?.dateISO || snapshot?.when?.dateISO || ""
  ).trim();
  const timeText = String(
    snapshot?.selectedTime || snapshot?.start || snapshot?.when?.time || snapshot?.when?.start || ""
  ).trim();
  if (!dateISO || !timeText) return NaN;

  const merged = new Date(`${dateISO}T${timeText}:00`);
  return merged.getTime();
}

const APPOINTMENT_WATERMARK_CLASS =
  "text-headline-medium font-semibold uppercase tracking-[0.12em] text-[rgba(0,189,196,0.34)]";

async function deleteAppointmentInDb(appointmentId) {
  const { error } = await supabase
    .from("appointments")
    .delete()
    .eq("id", appointmentId);
  if (error) throw error;
}

export default function AppointmentDetails() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { t, formatDate } = useLanguage();

  const [toastMsg, setToastMsg] = React.useState("");
  const [joiningVideo, setJoiningVideo] = React.useState(false);

  function readAndClearToastMessage(appointmentId, navState) {
    // 1) Route-state flags (one-time)
    if (navState?.justRequestedReschedule) {
      return t(
        "appointments.details.toast.rescheduleRequested",
        "New time request sent. Pending for approval."
      );
    }
    if (navState?.justConfirmed) {
      return t("appointments.details.toast.confirmed", "Appointment confirmed");
    }
    if (navState?.justRescheduled) {
      return t("appointments.details.toast.updated", "Appointment updated");
    }
    if (navState?.justDeleted) {
      return t("appointments.details.toast.deleted", "Your appointment was deleted");
    }

    // 2) Session flags (one-time)
    try {
      const keys = [
        [
          `afrimed:toast:appointmentRescheduleRequested:${String(appointmentId)}`,
          t(
            "appointments.details.toast.rescheduleRequested",
            "New time request sent. Pending for approval."
          ),
        ],
        [
          `afrimed:toast:appointmentConfirmed:${String(appointmentId)}`,
          t("appointments.details.toast.confirmed", "Appointment confirmed"),
        ],
        [
          `afrimed:toast:appointmentRescheduled:${String(appointmentId)}`,
          t("appointments.details.toast.updated", "Appointment updated"),
        ],
        [
          `afrimed:toast:appointmentDeleted:${String(appointmentId)}`,
          t("appointments.details.toast.deleted", "Your appointment was deleted"),
        ],
      ];

      for (const [k, msg] of keys) {
        const v = sessionStorage.getItem(k);
        if (v) {
          sessionStorage.removeItem(k);
          return msg;
        }
      }
    } catch {}

    return "";
  }

  React.useEffect(() => {
    const msg = readAndClearToastMessage(id, location.state || null);
    if (!msg) return;

    setToastMsg(msg);
    const t = setTimeout(() => setToastMsg(""), 3500);
    return () => clearTimeout(t);
  }, [id, location.state]);

  // Strip one-time flags before persisting
  const stateForRestore = React.useMemo(() => {
    const s = location.state || null;
    if (!s) return null;
    const { justConfirmed: _jc, justRequestedReschedule: _jrr, justRescheduled: _jr, justDeleted: _jd, ...rest } = s;
    return rest;
  }, [location.state]);

  const restored = React.useMemo(
    () => restoreAppointmentDraft(id, stateForRestore),
    [id, stateForRestore]
  );

  const [dbRestored, setDbRestored] = React.useState(null);
  const [dbHydrating, setDbHydrating] = React.useState(false);
  const [ehrAccessState, setEhrAccessState] = React.useState(null);
  const [grantingEhrAccess, setGrantingEhrAccess] = React.useState(false);
  const [liveSessionSnapshot, setLiveSessionSnapshot] = React.useState(null);
  const [, setFavoriteVersion] = React.useState(0);

  React.useEffect(() => subscribeDoctorFavorites(() => setFavoriteVersion((value) => value + 1)), []);

  React.useEffect(() => {
    let alive = true;

    async function hydrateFromDb() {
      if (!id) return;
      setDbHydrating(true);
      try {
        const snapshot = await loadPatientAppointmentSnapshot(id, { fallback: restored });
        if (!alive) return;
        setDbRestored(snapshot);
      } catch (error) {
        if (!alive) return;
        setDbRestored(null);
        if (!restored) {
          setToastMsg(
            error?.message ||
              t("appointments.details.errors.load", "Unable to load appointment.")
          );
        }
      } finally {
        if (!alive) return;
        setDbHydrating(false);
      }
    }

    hydrateFromDb();
    return () => {
      alive = false;
    };
  }, [id, restored]);

  React.useEffect(() => {
    let alive = true;
    let pollTimer = 0;

    async function refreshSessionState() {
      if (!id) return;

      try {
        const { data, error } =
          await videoConsultationService.getAppointmentSessionSnapshot(id);
        if (!alive || error) return;
        setLiveSessionSnapshot(data || null);
      } catch {
        if (!alive) return;
        setLiveSessionSnapshot(null);
      }
    }

    refreshSessionState();

    pollTimer = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      refreshSessionState();
    }, 10_000);

    return () => {
      alive = false;
      window.clearInterval(pollTimer);
    };
  }, [id]);

  const hydrated = React.useMemo(
    () => mergeAppointmentSnapshot(dbRestored, restored),
    [dbRestored, restored]
  );
  const careSubjectType = String(
    hydrated?.careSubjectType ||
      hydrated?.care_subject_type ||
      hydrated?.patient?.care_subject_type ||
      "self",
  )
    .trim()
    .toLowerCase();
  const careSubjectName = String(
    hydrated?.careSubjectName ||
      hydrated?.care_subject_name ||
      hydrated?.patient?.name ||
      "",
  ).trim();
  const guardianName = String(
    hydrated?.guardianName ||
      hydrated?.guardian_name ||
      hydrated?.patient?.guardian_name ||
      "",
  ).trim();
  const responsibleRelationship = String(
    hydrated?.careSubjectRelationship ||
      hydrated?.care_subject_relationship ||
      hydrated?.responsible_relationship ||
      hydrated?.patient?.responsible_relationship ||
      "",
  ).trim();

  React.useEffect(() => {
    if (hydrated) persistAppointment(id, hydrated);
  }, [id, hydrated]);

  React.useEffect(() => {
    let alive = true;

    async function hydrateEhrAccessState() {
      const doctorId = String(hydrated?.doctor?.id || "").trim();
      if (!id || !doctorId) {
        if (alive) setEhrAccessState(null);
        return;
      }

      try {
        const { data: authData, error: authError } = await supabase.auth.getUser();
        const patientUserId = String(authData?.user?.id || "").trim();
        if (authError || !patientUserId) {
          if (alive) setEhrAccessState(null);
          return;
        }

        const state = await loadPatientAppointmentEhrAccessState({
          appointmentId: id,
          doctorId,
          patientUserId,
        });

        if (alive) {
          setEhrAccessState(state);
        }
      } catch (error) {
        if (alive) {
          console.warn("Unable to load EHR access state:", error);
          setEhrAccessState(null);
        }
      }
    }

    hydrateEhrAccessState();
    return () => {
      alive = false;
    };
  }, [id, hydrated?.doctor?.id]);

  const mode = React.useMemo(() => normalizeMode(hydrated), [hydrated]);

  const doctor = React.useMemo(() => {
    if (!hydrated?.doctor) return null;
    const favoriteDoctorIds = [
      hydrated.doctor.id,
      hydrated.doctor.user_id,
      hydrated.doctor.doctor_user_id,
      hydrated.doctor.profile_id,
    ].filter(Boolean);
    return {
      ...hydrated.doctor,
      isFavorite: isFavoriteDoctor(favoriteDoctorIds),
    };
  }, [hydrated]);

  const selectedDate =
    hydrated?.selectedDate ?? hydrated?.when?.dateISO ?? hydrated?.when?.date ?? null;

  const selectedTime =
    hydrated?.selectedTime ?? hydrated?.when?.time ?? null;
  const lifecycleBucket = React.useMemo(
    () => getAppointmentLifecycleBucket(hydrated || {}, Date.now()),
    [hydrated]
  );
  const isPastInteractionDisabled = lifecycleBucket === "past";
  const appointmentStatus = normalizeAppointmentStatus(hydrated?.status);
  const isCompletedAppointment = appointmentStatus === "completed";
  const appointmentStartMs = React.useMemo(() => resolveAppointmentStartMs(hydrated), [hydrated]);
  const videoJoinGraceMs = 15 * 60 * 1000;
  const isWithinVideoJoinWindow =
    mode === "evisit" &&
    Number.isFinite(appointmentStartMs) &&
    Date.now() >= appointmentStartMs &&
    Date.now() <= appointmentStartMs + videoJoinGraceMs;
  const liveVideoStatus = String(
    liveSessionSnapshot?.appointment?.video_status ||
      liveSessionSnapshot?.session?.status ||
      ""
  )
    .trim()
    .toLowerCase();
  const consultationSessionEnded = Boolean(
    liveSessionSnapshot?.session?.ended_at ||
      ["completed", "ended", "closed"].includes(liveVideoStatus),
  );
  const clinicianReady = Boolean(
    liveSessionSnapshot?.session?.doctor_joined_at
  );
  const canOpenAppointmentCall =
    mode === "evisit" &&
    appointmentStatus !== "cancelled" &&
    appointmentStatus !== "completed" &&
    appointmentStatus !== "missed" &&
    clinicianReady &&
    (appointmentStatus === "in_progress" ||
      isWithinVideoJoinWindow ||
      liveVideoStatus === "in_progress");
  const isVideoJoinWindowExpired =
    mode === "evisit" &&
    appointmentStatus !== "in_progress" &&
    appointmentStatus !== "cancelled" &&
    Number.isFinite(appointmentStartMs) &&
    Date.now() > appointmentStartMs + videoJoinGraceMs;
  const appointmentStatusReason = String(hydrated?.appointmentStatusReason || "")
    .trim()
    .toLowerCase();
  const cancellationRequestState = String(hydrated?.cancellationRequestState || "")
    .trim()
    .toLowerCase();
  const isDeclinedAppointment =
    appointmentStatus === "cancelled" && appointmentStatusReason === "declined";
  const isPatientCancelledAppointment =
    appointmentStatus === "cancelled" && appointmentStatusReason === "cancelled";
  const isWatermarkedCancelledAppointment =
    isDeclinedAppointment || isPatientCancelledAppointment;
  const isCancellationRequestPending =
    appointmentStatus !== "cancelled" && cancellationRequestState === "requested";
  const isCancellationRequestExpired =
    isCancellationRequestPending && isPastInteractionDisabled;

  const paymentMethodLabel =
    hydrated?.paymentMethodLabel || hydrated?.paymentLabel || "Debit / Credit cards";
  const insuranceContextLabel = resolveInsuranceContextLabel(hydrated, t);
  const paymentDisplayState = String(hydrated?.paymentDisplayState || "").trim().toLowerCase();
  const paymentLedgerStatus = String(hydrated?.paymentLedgerStatus || "").trim().toLowerCase();
  const paymentRefundStatus = String(hydrated?.paymentMeta?.stripe_refund_status || "")
    .trim()
    .toLowerCase();
  const appointmentTimeWatermark = isDeclinedAppointment
    ? t("appointments.details.status.declined", "DECLINED")
    : isPatientCancelledAppointment
      ? t("appointments.details.status.cancelled", "CANCELLED")
      : "";
  const paymentWatermarkLabel =
    paymentDisplayState === "refunded"
      ? t("appointments.details.payment.refunded", "REFUNDED")
      : paymentDisplayState === "refund_pending"
      ? t("appointments.details.payment.refundPending", "REFUND PENDING")
      : paymentDisplayState === "refund_failed"
        ? t("appointments.details.payment.refundFailed", "REFUND FAILED")
      : paymentDisplayState === "reversal_attention"
          ? t("appointments.details.payment.reversalAttention", "REVERSAL ATTENTION")
      : paymentDisplayState === "reversal_required"
        ? t("appointments.details.payment.reversalRequired", "REFUND REQUIRED")
        : isWatermarkedCancelledAppointment && paymentDisplayState
          ? t("appointments.details.payment.cancelled", "CANCELLED")
          : "";
  const paymentStatusHelperText =
    paymentDisplayState === "refunded"
      ? t("appointments.details.payment.refundedHelp", "Payment has been reversed back to the original payment method.")
      : paymentDisplayState === "refund_pending"
      ? t(
          "appointments.details.payment.refundPendingHelp",
          "Refund is being processed back to the original payment method."
        )
      : paymentDisplayState === "refund_failed"
        ? t("appointments.details.payment.refundFailedHelp", "The provider did not complete the payment reversal. No return of funds has been confirmed.")
        : paymentDisplayState === "reversal_attention"
          ? t("appointments.details.payment.reversalAttentionHelp", "Payment reversal requires platform attention. No return of funds has been confirmed.")
      : paymentDisplayState === "reversal_required"
        ? t(
            "appointments.details.payment.reversalRequiredHelp",
            "Payment reversal still needs platform attention."
          )
        : paymentWatermarkLabel === t("appointments.details.payment.cancelled", "CANCELLED") &&
            (paymentLedgerStatus === "refunded" || paymentRefundStatus === "succeeded")
          ? t(
              "appointments.details.payment.refundedHelp",
              "Payment has been reversed back to the original payment method."
            )
          : paymentWatermarkLabel
            ? t(
                "appointments.details.payment.cancelledHelp",
                "Payment collection for this appointment has been cancelled."
              )
            : "";

  const amountPaid = hydrated?.amountPaid;
  const currency = hydrated?.currency || hydrated?.doctor?.currency || "ZAR";
  const ehrAccessStatus = ehrStatusFromServer(ehrAccessState?.request || ehrAccessState);
  const showEhrAccessCard = ehrAccessStatus === "requested" || ehrAccessStatus === "granted";
  const doctorDisplayName = resolvePatientFacingDoctorName(doctor, doctor?.name);

  const meetUrl = hydrated?.meetUrl || "";
  const videoRoomLabel = canOpenAppointmentCall
    ? clinicianReady
      ? t(
          "appointments.details.videoRoomReady",
          "Clinician is ready. Join your secure AfriMed consultation room."
        )
      : t("appointments.details.videoRoomLabel", "Secure AfriMed™ consultation room")
    : mode === "evisit" && isWithinVideoJoinWindow && !clinicianReady
      ? t(
          "appointments.details.videoRoomWaitingForClinician",
          "Your clinician has not started the video consultation yet."
        )
    : isVideoJoinWindowExpired
      ? t(
          "appointments.details.videoRoomClosed",
          "This appointment is no longer available because the booked video window has passed."
        )
    : appointmentStatus === "confirmed"
      ? t(
          "appointments.details.videoRoomLockedConfirmed",
          "Video room opens when the appointment starts."
        )
      : t(
          "appointments.details.videoRoomLockedPending",
          "Video room stays locked until the appointment is confirmed."
        );
  const joinButtonLabel = canOpenAppointmentCall
    ? clinicianReady
      ? t("appointments.details.joinReady", "Join consultation")
      : t("appointments.details.join", "Join")
    : mode === "evisit" && isWithinVideoJoinWindow && !clinicianReady
      ? t("appointments.details.joinWaitingForClinician", "Waiting for clinician")
    : isVideoJoinWindowExpired
      ? t("appointments.details.joinClosed", "Window closed")
    : appointmentStatus === "confirmed"
      ? t("appointments.details.joinLockedConfirmed", "Opens at start")
      : t("appointments.details.joinLockedPending", "Awaiting approval");

  const facilityName =
    hydrated?.facilityName || hydrated?.facility?.name || "Choose a facility";

  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [missedDialogOpen, setMissedDialogOpen] = React.useState(false);

  function handleBack() {
    if (window.history.length > 1) nav(-1);
    else nav("/appointments");
  }

  function handleMessageClick() {
    if (isPastInteractionDisabled) return;
    const doctorUserId = doctor?.user_id || doctor?.doctor_user_id || doctor?.id;
    if (!doctorUserId) {
      setToastMsg("The clinician identity is unavailable. Refresh the appointment before messaging.");
      return;
    }

    nav(`/messages/${doctorUserId}`, {
      state: {
        conversationTitle: doctorDisplayName,
        conversationType: "doctor",
        appointmentId: String(id),
        doctorId: doctorUserId,
        participantUserId: doctorUserId,
        returnTo: location.pathname,
      },
    });
  }

  function handleCallClick() {
    if (isPastInteractionDisabled) return;

    if (isVideoJoinWindowExpired) {
      setMissedDialogOpen(true);
      return;
    }

    if (mode === "evisit" && !clinicianReady) {
      setToastMsg(
        t(
          "appointments.details.toast.clinicianNotStartedVideo",
          "Your clinician has not started the video consultation yet."
        )
      );
      return;
    }

    if (!canOpenAppointmentCall) {
      setToastMsg(
        t(
          "appointments.details.toast.callOnlyDuringAppointment",
          "Only available for appointments"
        )
      );
      return;
    }

    nav(`/appointments/call/${id}`, {
      state: {
        appointmentId: String(id),
        meetUrl,
        doctor,
        doctorName: doctorDisplayName,
        counterpartName: doctorDisplayName,
        actorRole: "patient",
        mode,
        returnTo: location.pathname,
      },
    });
  }

  async function handleVideoJoinClick() {
    if (isVideoJoinWindowExpired) {
      setMissedDialogOpen(true);
      return;
    }

    if (!clinicianReady) {
      const message = t(
        "appointments.details.toast.clinicianNotStartedVideo",
        "Your clinician has not started the video consultation yet."
      );
      logVideoConsultationStartDebug({
        appointmentId: String(id || ""),
        appointmentStatus,
        hasVideoSession: Boolean(liveSessionSnapshot?.session?.id),
        videoSessionStatus: liveVideoStatus,
        action: "patient_join_blocked_waiting_for_doctor",
      });
      setToastMsg(message);
      return;
    }

    if (!canOpenAppointmentCall) return;

    setJoiningVideo(true);
    try {
      await primeConsultationMediaPermission({ appointmentId: id });
      nav(`/appointments/video/${id}`, {
        state: {
          appointmentId: String(id),
          meetUrl,
          doctor,
          doctorName: doctorDisplayName,
          counterpartName: doctorDisplayName,
          mode,
          mediaPermissionPrimed: true,
          returnTo: location.pathname,
        },
      });
    } catch (permissionError) {
      logVideoConsultationStartDebug({
        appointmentId: String(id || ""),
        appointmentStatus,
        hasVideoSession: Boolean(liveSessionSnapshot?.session?.id),
        videoSessionStatus: liveVideoStatus,
        action: "patient_join",
        errorName: permissionError?.name || "",
        errorMessage: permissionError?.message || String(permissionError || ""),
      });
      setToastMsg(
        permissionError?.message ||
          t(
            "appointments.details.toast.cameraPermissionRequired",
            "Allow camera and microphone access to join the consultation."
          )
      );
    } finally {
      setJoiningVideo(false);
    }
  }

  async function handleGrantEhrAccess() {
    const doctorId = String(doctor?.id || "").trim();
    if (!id || !doctorId) {
      setToastMsg(
        t(
          "appointments.details.errors.ehrMissing",
          "Missing appointment details for EHR access."
        )
      );
      return;
    }

    setGrantingEhrAccess(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      const patientUserId = String(authData?.user?.id || "").trim();
      if (authError || !patientUserId) {
        throw authError ||
          new Error(
            t(
              "appointments.details.errors.verifyAccount",
              "Could not verify your account."
            )
          );
      }

      const result = await grantAppointmentEhrAccess({
        appointmentId: id,
        doctorId,
        doctorRef: doctorId,
        patientUserId,
      });

      const nextSnapshot = {
        ...(hydrated || {}),
        ehrAccessStatus: "granted",
        ehrGrantedAt: result?.grantedAt || new Date().toISOString(),
      };

      setDbRestored(nextSnapshot);
      setEhrAccessState({
        status: "granted",
        request: null,
        permission: {
          granted_at: result?.grantedAt || new Date().toISOString(),
        },
      });
      persistAppointment(id, nextSnapshot);
      setToastMsg(
        t("appointments.details.toast.ehrGranted", "EHR access granted")
      );
    } catch (error) {
      console.warn("Unable to grant EHR access:", error);
      setToastMsg(
        error?.message ||
          t(
            "appointments.details.errors.ehrGrant",
            "Could not grant EHR access right now."
          )
      );
    } finally {
      setGrantingEhrAccess(false);
    }
  }

  async function handleCancelAppointment() {
    const appointmentId = String(id || "").trim();
    if (!appointmentId) return;

    if (isCancellationRequestPending) {
      setToastMsg(
        t(
          "appointments.details.toast.cancellationPending",
          "Cancellation request already sent. Pending doctor approval."
        )
      );
      return;
    }

    const appointmentDateLabel =
      selectedDate && selectedTime
        ? `${formatDate(selectedDate, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
          })} ${selectedTime}`
        : "the scheduled appointment";

    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      const currentPatientUserId = String(currentUser?.id || "").trim();
      if (!currentPatientUserId) {
        throw new Error(
          t(
            "appointments.details.errors.verifyAccount",
            "Could not verify your account."
          )
        );
      }

      const patientDisplayName =
        String(
          currentUser?.user_metadata?.full_name ||
            [
              currentUser?.user_metadata?.first_name,
              currentUser?.user_metadata?.last_name,
            ]
              .filter(Boolean)
              .join(" ") ||
            currentUser?.user_metadata?.display_name ||
            "Patient"
        ).trim() || "Patient";

      const nextSnapshot = {
        ...(hydrated || {}),
        cancellationRequestState: "requested",
      };

      setDbRestored(nextSnapshot);
      persistAppointment(appointmentId, nextSnapshot);

      if (currentPatientUserId) {
        await createAppointmentNotification({
          userId: currentPatientUserId,
          title: "Cancellation request sent",
          message: `Your request to cancel the appointment for ${appointmentDateLabel} was sent.`,
          type: `appointment_cancellation_request:${appointmentId}:requested`,
          priority: "urgent",
        });
      }

      const resolvedDoctorUserId = String(
        await resolveDoctorAuthUserId({
          appointmentId,
          doctorUserId: String(hydrated?.doctor?.user_id || hydrated?.doctorUserId || "").trim(),
          doctorRef: String(hydrated?.doctor?.id || hydrated?.doctorId || doctor?.id || "").trim(),
        }),
      ).trim();

      if (resolvedDoctorUserId) {
        await createAppointmentNotification({
          userId: resolvedDoctorUserId,
          title: "Cancellation request",
          message: `${patientDisplayName} requested to cancel appointment for ${appointmentDateLabel}. Pending for approval.`,
          type: `appointment_cancellation_request:${appointmentId}`,
          priority: "urgent",
        });
      }

      try {
        await sendAppointmentEventEmail({
          appointmentId,
          eventType: "cancellation_requested",
        });
      } catch (emailError) {
        console.warn("Unable to send appointment cancellation email:", emailError);
      }

      window.dispatchEvent(new Event("afrimed:notifications:changed"));
      setToastMsg(
        t(
          "appointments.details.toast.cancellationRequested",
          "Cancellation request sent. Pending doctor approval."
        )
      );
    } catch (error) {
      if (hydrated) {
        setDbRestored(hydrated);
        persistAppointment(appointmentId, hydrated);
      }
      setToastMsg(
        error?.message ||
          t(
            "appointments.details.errors.cancel",
            "Unable to cancel appointment."
          )
      );
    }
  }

  if (!hydrated) {
    return (
      <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title={t("appointments.details.title", "Appointment details")}
            onBack={handleBack}
          />

          <Card className="mt-6 p-4 text-body-medium text-on-surface-var">
            {dbHydrating
              ? t("appointments.details.loading", "Loading appointment...")
              : t(
                  "appointments.details.notFound",
                  "Appointment not found. Please go back and open it again."
                )}
            <div className="mt-3">
              <Button fullWidth onClick={() => nav("/appointments")}>
                {t("appointments.details.backToAppointments", "Back to appointments")}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
        <TopBarCenter
          title={t("appointments.details.title", "Appointment details")}
          titleClassName="text-[20px] leading-7 md:text-[24px] md:leading-8 font-normal text-on-surface"
          wrapTitle
          onBack={handleBack}
        />

        <AppointmentReservationPanel appointmentId={id} />
        <div className="mt-3 flex justify-end">
          <ContextFeedbackLauncher
            section="appointments-details"
            context={{ surface: "appointments.details", appointmentId: id, mode }}
            entity={{ type: "appointment", id: id || null }}
          />
        </div>

        {careSubjectType === "dependant" && careSubjectName ? (
          <Card className="mt-4 px-4 py-4 text-body-medium text-on-surface-var">
            <div className="text-label-large text-on-surface-var">
              {t("appointments.details.patientSubject", "Patient")}
            </div>
            <div className="mt-1 text-body-large text-on-surface">{careSubjectName}</div>
            {guardianName ? (
              <div className="mt-1 text-body-small text-on-surface-var">
                {t("appointments.details.responsibleAdult", "Responsible adult")}:{" "}
                <span className="text-on-surface">
                  {guardianName}
                  {responsibleRelationship ? ` • ${responsibleRelationship}` : ""}
                </span>
              </div>
            ) : null}
          </Card>
        ) : null}

        {/* Toast (single-line snackbar w/ close affordance) */}
        {toastMsg ? (
          <div
            className="
              fixed left-1/2 -translate-x-1/2
              bottom-[92px] md:bottom-6
              w-full max-w-[720px] px-4
              z-[80]
            "
          >
            <div
              className="
                w-full
                rounded-[12px]
                bg-[#0B0F1A]
                text-white
                px-4 py-3
                flex items-center justify-between gap-3
                shadow-[0_10px_30px_rgba(2,6,23,0.35)]
              "
              role="status"
              aria-live="polite"
            >
              <div className="text-body-medium truncate">{toastMsg}</div>

              <button
                type="button"
                onClick={() => setToastMsg("")}
                aria-label={t("common.close", "Close")}
                className="
                  shrink-0
                  h-9 w-9
                  rounded-[10px]
                  bg-[rgba(255,255,255,0.08)]
                  hover:bg-[rgba(255,255,255,0.12)]
                  active:bg-[rgba(255,255,255,0.12)]
                  flex items-center justify-center
                "
              >
                <span className="text-white text-[18px] leading-none translate-y-[-1px]">×</span>
              </button>
            </div>
          </div>
        ) : null}

        {/* Doctor */}
        <div className="mt-4">
          {doctor ? (
            <DoctorResultCard
              doctor={doctor}
              naturalHeight
              onLocate={(payload) => {
                const qs = new URLSearchParams();
                const facilityName = String(payload?.name || doctor?.hospital || "").trim();
                const facilityAddress = String(payload?.address || doctor?.address || "").trim();
                if (facilityName) {
                  qs.set("facilityName", facilityName);
                }
                if (facilityAddress) qs.set("facilityAddress", facilityAddress);
                if (payload?.facilityId) qs.set("facilityId", String(payload.facilityId));
                if (Number.isFinite(Number(payload?.lat))) qs.set("facilityLat", String(Number(payload.lat)));
                if (Number.isFinite(Number(payload?.lng))) qs.set("facilityLng", String(Number(payload.lng)));
                nav(`/appointments/doctors?${qs.toString()}`);
              }}
              onToggleFavorite={() => {}}
            />
          ) : (
            <div className="opacity-60 text-body-medium text-on-surface-var">
              {t("appointments.details.appointmentId", "Appointment ID")}: {id}
            </div>
          )}
        </div>

        {/* Virtual join card only; in-person details are already covered in the doctor card */}
        {mode === "evisit" && !consultationSessionEnded ? (
          <Card className="mt-4 overflow-hidden">
            {clinicianReady && !isCompletedAppointment ? (
              <div className="border-b border-[rgba(var(--afrimed-primary),0.18)] bg-[rgba(var(--afrimed-primary),0.08)] px-4 py-3">
                <div className="text-label-large text-[rgb(var(--afrimed-primary))]">
                  {t(
                    "appointments.details.videoDoctorStartedTitle",
                    "The doctor has started the consultation."
                  )}
                </div>
                <div className="mt-0.5 text-body-medium text-on-surface-var">
                  {t(
                    "appointments.details.videoDoctorStartedCopy",
                    "Join from here to allow camera and microphone access before entering the room."
                  )}
                </div>
              </div>
            ) : null}
            <div className="px-4 py-4 flex flex-wrap items-center gap-3">
              <img
                src={ICONS.evisit}
                alt=""
                className="h-6 w-6 shrink-0 opacity-80"
                draggable="false"
              />

              <div className="flex-1 min-w-[180px]">
                <div className="text-label-medium text-on-surface-var">
                  {t("appointments.details.virtualLabel", "Virtual")}
                </div>
                <div className="text-body-large text-on-surface -mt-0.5">
                  {t("appointments.details.meetLabel", "AfriMed™ video")}
                </div>
                <div className="text-body-medium text-on-surface-var">{videoRoomLabel}</div>
              </div>

              <Button
                className="h-10 w-full sm:w-auto px-4 rounded-[12px] text-label-large text-[rgb(var(--afrimed-on-primary))]"
                disabled={
                  joiningVideo ||
                  isPastInteractionDisabled ||
                  (!canOpenAppointmentCall && !isVideoJoinWindowExpired)
                }
                onClick={handleVideoJoinClick}
              >
                {joiningVideo
                  ? t("appointments.details.joinConnecting", "Opening...")
                  : joinButtonLabel}
              </Button>
            </div>
          </Card>
        ) : null}

        {/* Message / Call */}
        <div className="mt-4 flex gap-4">
          <Button
            type="button"
            variant="ghost"
            disabled={isPastInteractionDisabled || !(doctor?.user_id || doctor?.doctor_user_id || doctor?.id)}
            className="flex-1 h-12 gap-2 rounded-[12px] text-label-large text-[rgb(var(--afrimed-primary))]"
            onClick={handleMessageClick}
          >
            <img src={ICONS.chat} alt="" className="w-[18px] h-[18px]" draggable="false" />
            {t("appointments.details.message", "Message")}
          </Button>

          <Button
            type="button"
            variant="ghost"
            disabled={isPastInteractionDisabled || !canOpenAppointmentCall}
            title={`Internet audio call. ${videoRoomLabel}`}
            className="flex-1 h-12 gap-2 rounded-[12px] text-label-large text-[rgb(var(--afrimed-primary))]"
            onClick={handleCallClick}
          >
            <img src={ICONS.call} alt="" className="w-[24px] h-[24px]" draggable="false" />
            {t("appointments.details.call", "Call")}
          </Button>
        </div>

        {showEhrAccessCard ? (
          <Card className="mt-4 overflow-hidden">
            <div className="px-4 py-4 flex items-center gap-4">
              {ehrAccessStatus === "granted" ? (
                <>
                  <img
                    src={ICONS.grantedEhr}
                    alt=""
                    className="h-6 w-6 shrink-0"
                    draggable="false"
                  />
                  <div className="min-w-0 flex-1 text-title-medium text-on-surface">
                    {t("appointments.details.ehr.title", "EHR")}
                  </div>
                  <div className="flex items-center justify-end gap-2 text-title-medium text-on-surface-var">
                    <span>{t("appointments.details.ehr.granted", "Access granted")}</span>
                    <img
                      src={ICONS.requestGranted}
                      alt=""
                      className="h-6 w-6 shrink-0"
                      draggable="false"
                    />
                  </div>
                </>
              ) : (
                <>
                  <img
                    src={ICONS.requestEhr}
                    alt=""
                    className="h-16 w-16 shrink-0"
                    draggable="false"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-title-medium text-on-surface">
                      {t("appointments.details.ehr.grant", "Grant access")}
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleGrantEhrAccess}
                    disabled={grantingEhrAccess}
                    className="h-10 px-4 rounded-[12px] text-label-large whitespace-nowrap bg-[rgb(var(--afrimed-primary))] text-[rgb(var(--afrimed-on-primary))]"
                  >
                    {grantingEhrAccess
                      ? t("appointments.details.ehr.granting", "Granting...")
                      : t("appointments.details.ehr.grant", "Grant access")}
                  </Button>
                </>
              )}
            </div>
          </Card>
        ) : null}

        {/* Appointment time */}
        <div className="mt-6">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t(
              "appointments.details.sections.time",
              "Your appointment time"
            )}
          </div>

          <Card className="relative overflow-hidden">
            <div className="px-4 py-4 flex flex-wrap items-center gap-3">
              <div className="h-9 px-6 rounded-[12px] bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))] text-label-large flex items-center">
                {selectedTime || "—"}
              </div>

              <div className="text-body-large text-on-surface">
                {selectedDate
                  ? formatDate(selectedDate, {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })
                  : "—"}
              </div>
            </div>

            {appointmentTimeWatermark ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
                <span className={APPOINTMENT_WATERMARK_CLASS}>
                  {appointmentTimeWatermark}
                </span>
              </div>
            ) : null}
          </Card>
        </div>

        {/* Payment */}
        <div className="mt-6">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("appointments.details.sections.payment", "Payment method")}
          </div>

          <Card className="relative overflow-hidden">
            <div className="px-4 py-4 flex flex-wrap items-center gap-3">
              <img src={ICONS.card} alt="" className="w-6 h-6 opacity-80" draggable="false" />
              <div className="min-w-0 flex-1">
                <div className="text-body-large text-on-surface">{paymentMethodLabel}</div>
                {paymentStatusHelperText ? (
                  <div className="mt-1 text-body-small text-on-surface-var">
                    {paymentStatusHelperText}
                  </div>
                ) : null}
                <div className="mt-1 text-body-small text-on-surface-var">
                  {insuranceContextLabel}
                </div>
              </div>

              {typeof amountPaid === "number" ? (
                <div className="text-body-large text-on-surface-var">
                  {amountPaid.toFixed(2)} {currency}
                </div>
              ) : null}
            </div>

            {paymentWatermarkLabel ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
                <span className={APPOINTMENT_WATERMARK_CLASS}>
                  {paymentWatermarkLabel}
                </span>
              </div>
            ) : null}
          </Card>
        </div>

        {/* Bottom CTAs */}
        <div className="mt-6 flex gap-4">
          <button
            type="button"
            className="
              flex-1 h-12 rounded-[12px]
              bg-[var(--state-critical-08)]
              text-[rgb(var(--schemes-critical))]
              text-label-large
              disabled:cursor-not-allowed disabled:opacity-45
            "
            disabled={isPastInteractionDisabled || isCancellationRequestPending}
            onClick={() => {
              if (isPastInteractionDisabled || isCancellationRequestPending) return;
              setCancelOpen(true);
            }}
          >
            {isCancellationRequestExpired
              ? t("appointments.details.cancelVisitExpired", "Cancellation expired")
              : isCancellationRequestPending
              ? t("appointments.details.cancelVisitPending", "Cancellation pending")
              : t("appointments.details.cancelVisit", "Cancel visit")}
          </button>

          <button
            type="button"
            className="
              flex-1 h-12 rounded-[12px]
              bg-[rgba(var(--afrimed-primary),0.12)]
              text-[rgb(var(--afrimed-primary))]
              text-label-large
              disabled:cursor-not-allowed disabled:opacity-100
            "
            disabled={isPastInteractionDisabled}
            onClick={() => {
              if (isPastInteractionDisabled) return;
              nav(`/appointments/details/${id}/edit`, { state: hydrated || null });
            }}
          >
            {t("appointments.details.editVisit", "Edit visit")}
          </button>
        </div>

        {isCancellationRequestPending ? (
          <div className="mt-3 text-body-small text-on-surface-var">
            {isCancellationRequestExpired
              ? t(
                  "appointments.details.cancellationExpiredHelp",
                  "This appointment time has passed, so the cancellation request is no longer active."
                )
              : t(
                  "appointments.details.cancellationPendingHelp",
                  "Your doctor still needs to approve this cancellation. The appointment remains active until then."
                )}
          </div>
        ) : null}

        <AppointmentCancelDialog
          open={cancelOpen}
          title={t(
            "appointments.details.cancelDialog.title",
            "Send cancellation request?"
          )}
          description={t(
            "appointments.details.cancelDialog.description",
            "Your doctor must approve this cancellation before the appointment is cancelled and any refund is processed."
          )}
          cancelText={t("common.no", "No")}
          confirmText={t(
            "appointments.details.cancelDialog.confirm",
            "Yes, send request"
          )}
          onCancel={() => setCancelOpen(false)}
          onConfirm={async () => {
            setCancelOpen(false);
            await handleCancelAppointment();
          }}
        />

        {/* Delete dialog is still reachable from Edit Menu (Delete appointment). 
            We keep this here only as a safety net if you call setDeleteOpen(true) later. */}
        <AppointmentDeleteDialog
          open={deleteOpen}
          title={t(
            "appointments.details.deleteDialog.title",
            "Are you sure you want to delete this medical appointment?"
          )}
          description={t(
            "appointments.details.deleteDialog.description",
            "After deleting the medical appointment record, you will not be able to recover it."
          )}
          cancelText={t("common.no", "No")}
          confirmText={t("appointments.details.deleteDialog.confirm", "Delete")}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            setDeleteOpen(false);

            try {
              await deleteAppointmentInDb(id);
              sessionStorage.removeItem(`afrimed:appointment:${String(id)}`);
              window.dispatchEvent(new Event("afrimed:appointments:changed"));
            } catch {}

            try {
              // ✅ generic toast key for the Appointments page
              sessionStorage.setItem("afrimed:toast:appointments:deleted", "1");
              localStorage.setItem("afrimed:toast:appointments:deleted", "1");
            } catch {}

            nav("/appointments?toast=deleted", {
              replace: true,
              state: { justDeleted: true, deletedId: String(id) },
            });
          }}
        />

        <AppointmentMissedDialog
          open={missedDialogOpen}
          title={t("appointments.details.missedDialog.title", "Missed appointment")}
          description={t(
            "appointments.details.missedDialog.description",
            "Participants for this appointment did not arrive within the time booked and allocated. This appointment is no longer available. If you would still like an appointment, please choose an available time and make a fresh booking. The new booking will require payment to be confirmed."
          )}
          closeText={t("common.close", "Close")}
          onClose={() => setMissedDialogOpen(false)}
        />
      </div>
    </div>
  );
}
